// Sopwith 3D - game driver: level setup, the tick loop and end-of-round logic.
//
// Copyright (C) 1984-2000 David L. Clark
// Copyright (C) 2001-2026 Simon Howard and SDL Sopwith contributors
// Copyright (C) 2026 Sopwith 3D contributors
//
// This program is free software; you can redistribute it and/or modify it
// under the terms of the GNU General Public License as published by the Free
// Software Foundation; either version 2 of the License, or (at your option)
// any later version. This program is distributed WITHOUT ANY WARRANTY.
//
// Ported from SDL Sopwith swmain.c / swinit.c / swend.c. `Game` owns all the
// state the C version kept in globals. It is deterministic: the same mission,
// config and command sequence always produce the same ticks.

import { swcollsn, type CollisionEvent } from './collision';
import {
  FPS,
  K_HARRYKEYS,
  MAX_NET_LAG,
  MAX_PLYR,
  MAX_SPEED,
  MAXCRASH,
  MAXFUEL,
  MIN_SPEED,
} from './constants';
import { imin } from './fixed';
import { initballoon, initflock, initox, initpowerup, inittarget } from './hazards';
import type { Mission } from './level';
import { ObjectWorld } from './objects';
import { initcomp, initplyr, planeIsKilled } from './plane';
import { buildSymbolTable, type SymbolTable } from './symbols';
import {
  EndStatus,
  Faction,
  type LevelData,
  Obj,
  ObjState,
  ObjType,
  newScore,
  type OriginalOb,
  PlayMode,
  type Score,
} from './types';
import { getEndLevel } from './medals';

const SUCCESSFUL_FLIGHT_TIME = 8 * FPS;

export interface GameConfig {
  playmode: PlayMode;
  /** Level to start at (the `-g#` option). */
  startingLevel: number;
  /** Missiles and starbursts available (the `-x` option). */
  missiles: boolean;
  /** Planes can be wounded rather than shot straight down. */
  wounded: boolean;
  /** Birds and oxen present. */
  animals: boolean;
  /** Original "Harry keys": pitch keys swap when the sprite is mirrored. */
  harrykeys: boolean;
  /**
   * Modern option: pull-up always moves the nose up on screen, even when
   * the plane is flying inverted. Off reproduces the original pilot-relative
   * elevator.
   */
  screenPitch: boolean;
  /**
   * Modern option: the plane rolls upright by itself once it is flying
   * level-ish, so it never cruises inverted after a loop. Enemy planes always
   * do this in the original.
   */
  autoRoll: boolean;
  medals: boolean;
  bigExplosions: boolean;
  /** Seed for the explosion pattern generator (the original used the clock). */
  seed: number;
}

export const DEFAULT_CONFIG: GameConfig = {
  playmode: PlayMode.SINGLE,
  startingLevel: 0,
  missiles: false,
  wounded: false,
  animals: true,
  harrykeys: false,
  screenPitch: true,
  autoRoll: true,
  medals: true,
  bigExplosions: true,
  seed: 7491,
};

export type GameEvent =
  | { kind: 'levelStart'; level: number }
  | { kind: 'levelComplete'; level: number; bonus: number }
  | { kind: 'gameOver'; score: number };

export class Game {
  readonly conf: GameConfig;
  playmode: PlayMode;
  readonly level: LevelData;
  /** Live terrain (craters modify it); reset from the level each round. */
  ground: Uint8Array;
  readonly symbols: SymbolTable;
  readonly world = new ObjectWorld();

  // Counters and per-level parameters.
  countmove = 0;
  gamenum = 0;
  gminspeed = MIN_SPEED;
  gmaxspeed = MAX_SPEED;
  targrnge = 0;
  keydelay = 1;
  dispcnt = 0;
  /** Explosion pattern generator state. */
  explseed = 0;
  /** Splats and windscreen hits raised this tick, for the HUD. */
  collisionEvents: CollisionEvent[] = [];

  // Planes and players.
  planes: Obj[] = [];
  numPlanes = 0;
  numPlayers = 0;
  /** Index of the local player. */
  player = 0;
  consoleplayer!: Obj;
  numtarg: number[] = new Array<number>(Faction.NUM_FACTIONS).fill(0);

  // Per-tick flags mirroring the C globals.
  plyrplane = false;
  compplane = false;
  endstat: EndStatus = EndStatus.PLAYING;
  endcount = 0;
  maxcrash = MAXCRASH;
  quit = false;
  private restartFlag = false;
  private ended = false;
  private lastGroundTime = 0;
  successfulFlight = false;

  // Lockstep command buffers, one ring per player.
  readonly commands: number[][] = [];
  readonly latestPlayerTime: number[] = [];

  private savedScore: Score | null = null;
  private origPlanes: OriginalOb[] = [];
  /** Events raised since the last call to `takeEvents()`. */
  private events: GameEvent[] = [];

  constructor(mission: Mission, config: Partial<GameConfig> = {}, base?: Mission) {
    this.conf = { ...DEFAULT_CONFIG, ...config };
    this.playmode = this.conf.playmode;
    const level = mission.level ?? base?.level;
    if (!level) {
      throw new Error('mission has no level');
    }
    this.level = level;
    const symbolTexts = new Map(base?.symbols ?? []);
    for (const [k, v] of mission.symbols ?? []) {
      symbolTexts.set(k, v);
    }
    this.symbols = buildSymbolTable(symbolTexts);
    this.ground = new Uint8Array(level.ground);
    this.explseed = this.conf.seed >>> 0 || 74917777;
    this.gamenum = this.conf.startingLevel;
    for (let i = 0; i < MAX_PLYR; i++) {
      this.commands.push(new Array<number>(MAX_NET_LAG).fill(0));
      this.latestPlayerTime.push(0);
    }
    this.initLevel();
  }

  // -------------------------------------------------------------------------
  // Level setup (swinitlevel)

  initLevel(): void {
    this.ground = new Uint8Array(this.level.ground);
    this.world.reset();
    this.numPlayers = 0;
    this.numPlanes = 0;
    this.planes = [];
    this.origPlanes = [];
    this.quit = false;
    this.restartFlag = false;

    let player1: OriginalOb | null = null;
    let player2: OriginalOb | null = null;
    for (const plane of this.level.objects) {
      if (plane.type !== ObjType.PLANE) {
        continue;
      }
      if (this.playmode === PlayMode.ASYNCH && plane.faction > Faction.PLAYER2) {
        continue;
      }
      if (plane.faction > Faction.PLAYER4) {
        continue; // player 5-8 planes are unused
      }
      this.origPlanes.push(plane);
      if (!player1 && plane.faction === Faction.PLAYER1) {
        player1 = plane;
      }
      if (!player2 && plane.faction === Faction.PLAYER2) {
        player2 = plane;
      }
    }
    if (!player1) {
      throw new Error('level has no PLAYER1 plane');
    }

    initplyr(this, null, player1);

    if (this.playmode === PlayMode.ASYNCH) {
      this.maxcrash = MAXCRASH * 2;
      if (!player2) {
        throw new Error('level has no PLAYER2 plane');
      }
      initplyr(this, null, player2);
    } else {
      this.maxcrash = MAXCRASH;
      player2 = null;
    }

    for (const orig of this.origPlanes) {
      if (orig !== player1 && orig !== player2) {
        initcomp(this, null, orig);
      }
    }

    this.inittargets();
    this.initgdep();

    this.countmove = 0;
    this.lastGroundTime = 0;
    this.successfulFlight = false;
    for (let i = 0; i < this.numPlayers; i++) {
      this.latestPlayerTime[i] = 0;
    }
    this.events.push({ kind: 'levelStart', level: this.gamenum });
  }

  private inittargets(): void {
    this.numtarg.fill(0);
    for (const orig of this.level.objects) {
      let ob: Obj | null;
      switch (orig.type) {
        case ObjType.TARGET:
          ob = inittarget(this, orig);
          break;
        case ObjType.OX:
          ob = initox(this, orig);
          break;
        case ObjType.FLOCK:
          ob = initflock(this, orig);
          break;
        case ObjType.BALLOON:
          ob = initballoon(this, orig);
          break;
        case ObjType.POWERUP:
          ob = initpowerup(this, orig);
          break;
        default:
          continue;
      }
      if (ob) {
        ob.originalOb = orig;
        this.world.insertx(ob, this.world.topobj);
      }
    }
  }

  /** Difficulty-dependent parameters. */
  private initgdep(): void {
    this.gmaxspeed = MAX_SPEED + this.gamenum;
    this.gminspeed = MIN_SPEED + this.gamenum;
    let r = 150;
    if (this.gamenum < 6) {
      r -= 15 * (6 - this.gamenum);
    }
    this.targrnge = r * r;
  }

  /** Score carried over from a completed level (initscore). */
  takeSavedScore(): Score {
    if (this.savedScore) {
      const s = this.savedScore;
      this.savedScore = null;
      return { ...s, medals: [...s.medals], ribbons: [...s.ribbons] };
    }
    return newScore();
  }

  // -------------------------------------------------------------------------
  // Tick loop

  /**
   * Record the local player's command word for the next tick and, when every
   * player's command is available, advance the simulation one tick.
   */
  tick(keys: number): boolean {
    if (this.ended) {
      return false;
    }
    if (this.restartFlag) {
      this.initLevel();
    }
    this.newMove(keys);
    if (!this.canMove()) {
      return false;
    }
    this.collisionEvents.length = 0;
    this.swmove();
    swcollsn(this);
    return true;
  }

  private newMove(keys: number): void {
    let multkey = keys;
    if (this.conf.harrykeys) {
      multkey |= K_HARRYKEYS;
    }
    const t = this.latestPlayerTime[this.player];
    this.commands[this.player][t % MAX_NET_LAG] = multkey;
    this.latestPlayerTime[this.player] = t + 1;
  }

  private canMove(): boolean {
    let lowtic = this.countmove + MAX_NET_LAG;
    for (let i = 0; i < this.numPlayers; i++) {
      lowtic = imin(lowtic, this.latestPlayerTime[i]);
    }
    return lowtic > this.countmove;
  }

  /** Move every object once (swmove). */
  private swmove(): void {
    this.world.recycleDeleted();

    this.dispcnt++;
    if (this.dispcnt >= this.keydelay) {
      this.dispcnt = 0;
    }

    this.world.forEach((ob) => {
      ob.drwflg = ob.movef ? ob.movef(this, ob) : false;
    });

    this.countmove++;
    if (this.consoleplayer.athome) {
      this.lastGroundTime = this.countmove;
    } else if (this.countmove - this.lastGroundTime > SUCCESSFUL_FLIGHT_TIME) {
      this.successfulFlight = true;
    }
  }

  // -------------------------------------------------------------------------
  // End of round (swend.c / swrestart)

  /** Called when one side has no targets left (CheckForWinner). */
  endgame(winningFaction: Faction): void {
    for (let ob = this.world.objtop; ob && ob.type === ObjType.PLANE; ob = ob.next) {
      if (ob.endsts !== EndStatus.PLAYING) {
        continue;
      }
      if (
        ob.faction === winningFaction &&
        (ob.crashcnt < MAXCRASH - 1 || (ob.crashcnt < MAXCRASH && !planeIsKilled(ob.state)))
      ) {
        this.winner(ob);
      } else {
        this.loser(ob);
      }
    }
  }

  winner(ob: Obj): void {
    ob.endsts = EndStatus.WINNER;
    ob.goingsun = true;
    ob.dx = ob.dy = ob.ldx = ob.ldy = 0;
    ob.state = ObjState.FLYING;
    ob.life = MAXFUEL;
    ob.speed = MIN_SPEED;
    if (ob === this.consoleplayer) {
      this.endcount = 72;
    }
  }

  loser(ob: Obj): void {
    ob.endsts = EndStatus.LOSER;
    if (ob === this.consoleplayer) {
      this.endcount = 20;
    }
  }

  /** The round is over: either advance a level or finish the game. */
  restart(): void {
    const ob = this.planes[this.player];
    if (this.consoleplayer.endsts === EndStatus.WINNER) {
      getEndLevel(ob);
      // Bonus for each unused plane, growing by 25 each time.
      let inc = 0;
      let bonus = 0;
      while (ob.crashcnt < this.maxcrash) {
        ob.crashcnt++;
        inc += 25;
        bonus += inc;
      }
      ob.score.score += bonus;
      this.events.push({ kind: 'levelComplete', level: this.gamenum, bonus });
      this.gamenum++;
      this.savedScore = ob.score;
    } else {
      this.events.push({ kind: 'gameOver', score: ob.score.score });
      this.gamenum = this.conf.startingLevel;
      this.savedScore = null;
      this.ended = true;
    }
    this.initgdep();
    this.restartFlag = true;
  }

  /** Hard stop (the original exited to DOS here). */
  end(): void {
    this.ended = true;
  }

  get isOver(): boolean {
    return this.ended;
  }

  takeEvents(): GameEvent[] {
    const e = this.events;
    this.events = [];
    return e;
  }
}
